// Aufmaß math, client twin of utils/measurements/geometry.js (live figures while editing; the
// server stays authoritative). Keep everything below the header identical to the server file:
// test/measurements/geometry.test.js compares both.

const SHAPES = ["rectangle", "quad", "polygon"];
const OPENING_KINDS = ["door", "window", "other"];
const ADJUSTMENT_TARGETS = ["floor", "wall", "ceiling"];
// Anything longer than 100 m in a room is a typo (a missing decimal separator).
const MAX_LENGTH = 100000;
const MAX_COUNT = 999;
const MAX_WALLS = 64;
const MAX_ITEMS = 100;
const MM2_PER_M2 = 1000000;
// A drawn outline that misses its starting corner by more than this has a measuring mistake.
const CLOSURE_TOLERANCE = 10;
// A checked diagonal that differs from the computed one by more than this points to a mistake.
const DIAGONAL_TOLERANCE = 10;

// When an opening or cut-out is deducted, per single piece ("Einzelgröße"), in m². VOB/C:
// painting, plaster and drywall (DIN 18363, 18350, 18340) deduct openings over 2.5 m²; tiles
// and floor coverings (DIN 18352, 18365) deduct cut-outs over 0.1 m². `wallDeductOver` also
// applies to ceilings. 0 deducts everything (exact net areas).
const DEDUCTION_PRESETS = {
  exact: { wallDeductOver: 0, floorDeductOver: 0 },
  painter: { wallDeductOver: 2.5, floorDeductOver: 2.5 },
  tiler: { wallDeductOver: 0.1, floorDeductOver: 0.1 }
};
const DEFAULT_RULES = { preset: "painter", ...DEDUCTION_PRESETS.painter };

const toLength = value => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.round(n), MAX_LENGTH);
};

const toCount = value => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, MAX_COUNT);
};

const toText = (value, max = 200) => (typeof value === "string" ? value.trim().slice(0, max) : "");

// Turn at the end of a wall, in degrees: +90 is a right-hand corner, -90 a left-hand one.
const toTurn = value => {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return 90;
  let turn = n % 360;
  if (turn > 180) turn -= 360;
  if (turn <= -180) turn += 360;
  return Math.round(turn * 100) / 100;
};

const normalizeRules = input => {
  const rules = input || {};
  const preset = Object.keys(DEDUCTION_PRESETS).includes(rules.preset) ? rules.preset : "custom";
  const threshold = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.min(n, 1000) : fallback;
  };
  if (preset !== "custom") return { preset, ...DEDUCTION_PRESETS[preset] };
  return {
    preset,
    wallDeductOver: threshold(rules.wallDeductOver, DEFAULT_RULES.wallDeductOver),
    floorDeductOver: threshold(rules.floorDeductOver, DEFAULT_RULES.floorDeductOver)
  };
};

/** Cleans one room from a form, a request or the voice assistant. Never throws. */
const normalizeRoom = input => {
  const room = input || {};
  const shape = SHAPES.includes(room.shape) ? room.shape : "rectangle";
  const sides = Array.isArray(room.sides) ? room.sides : [];
  return {
    ...(room._id ? { _id: room._id } : {}),
    name: toText(room.name, 120),
    level: toText(room.level, 60),
    shape,
    length: toLength(room.length),
    width: toLength(room.width),
    sides: [0, 1, 2, 3].map(index => toLength(sides[index])),
    diagonal: toLength(room.diagonal),
    diagonal2: toLength(room.diagonal2),
    walls: (Array.isArray(room.walls) ? room.walls : [])
      .slice(0, MAX_WALLS)
      .map(wall => ({ length: toLength(wall?.length), turn: toTurn(wall?.turn) })),
    height: toLength(room.height),
    openings: (Array.isArray(room.openings) ? room.openings : [])
      .slice(0, MAX_ITEMS)
      .map(opening => ({
        kind: OPENING_KINDS.includes(opening?.kind) ? opening.kind : "window",
        label: toText(opening?.label, 120),
        width: toLength(opening?.width),
        height: toLength(opening?.height),
        count: toCount(opening?.count),
        depth: toLength(opening?.depth)
      })),
    adjustments: (Array.isArray(room.adjustments) ? room.adjustments : [])
      .slice(0, MAX_ITEMS)
      .map(item => ({
        label: toText(item?.label, 120),
        target: ADJUSTMENT_TARGETS.includes(item?.target) ? item.target : "floor",
        sign: Number(item?.sign) < 0 ? -1 : 1,
        length: toLength(item?.length),
        width: toLength(item?.width),
        count: toCount(item?.count)
      })),
    notes: toText(room.notes, 2000)
  };
};

// Unit vector for a heading in degrees. Multiples of 90° are exact, so right-angled rooms
// stay whole millimetres.
const direction = degrees => {
  const d = ((degrees % 360) + 360) % 360;
  if (d === 0) return [1, 0];
  if (d === 90) return [0, 1];
  if (d === 180) return [-1, 0];
  if (d === 270) return [0, -1];
  const rad = (d * Math.PI) / 180;
  return [Math.cos(rad), Math.sin(rad)];
};

// Screen coordinates (y points down), so a positive turn is a right-hand (clockwise) corner.
const polygonFromWalls = walls => {
  const points = [[0, 0]];
  let heading = 0;
  walls.forEach(wall => {
    const [dx, dy] = direction(heading);
    const [x, y] = points[points.length - 1];
    points.push([x + wall.length * dx, y + wall.length * dy]);
    heading += wall.turn;
  });
  return points;
};

const shoelace = points => {
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
};

// Heron's formula in Kahan's numerically stable form. Null if the sides cannot form a
// triangle (a measuring mistake).
const triangleArea = (a, b, c) => {
  const [x, y, z] = [a, b, c].sort((p, q) => q - p);
  if (z <= 0 || x >= y + z) return null;
  return Math.sqrt((x + (y + z)) * (z - (x - y)) * (z + (x - y)) * (x + (y - z))) / 4;
};

/**
 * The missing wall that closes a free outline: its length and the turn to set on the current
 * last wall. Null when the outline already closes.
 */
const closingWall = walls => {
  if (!walls.length) return null;
  const points = polygonFromWalls(walls);
  const [x, y] = points[points.length - 1];
  const gap = Math.hypot(x, y);
  if (gap < 1) return null;
  const heading = walls.slice(0, -1).reduce((sum, wall) => sum + wall.turn, 0);
  const target = (Math.atan2(-y, -x) * 180) / Math.PI;
  return { length: Math.round(gap), turn: toTurn(target - heading) };
};

// Outline of the floor: area (mm²), perimeter (mm), corner points for the sketch.
const measureShape = (room, warnings) => {
  if (room.shape === "rectangle") {
    const { length, width } = room;
    if (!length || !width) {
      warnings.push({ code: "MISSING_DIMENSIONS" });
      return null;
    }
    return {
      area: length * width,
      perimeter: 2 * (length + width),
      points: [
        [0, 0],
        [length, 0],
        [length, width],
        [0, width]
      ]
    };
  }

  if (room.shape === "quad") {
    const [a, b, c, d] = room.sides;
    const e = room.diagonal;
    if (!a || !b || !c || !d || !e) {
      warnings.push({ code: "MISSING_DIMENSIONS" });
      return null;
    }
    const first = triangleArea(a, b, e);
    const second = triangleArea(c, d, e);
    if (first === null || second === null) {
      warnings.push({ code: "IMPOSSIBLE_SHAPE" });
      return null;
    }
    // A at the origin, B along the x axis, C from AB = a, BC = b, AC = e, and D on the other
    // side of the diagonal AC from B with CD = c, DA = d.
    const cx = (a * a + e * e - b * b) / (2 * a);
    const cy = Math.sqrt(Math.max(e * e - cx * cx, 0));
    const [ux, uy] = [cx / e, cy / e];
    const along = (e * e + d * d - c * c) / (2 * e);
    const across = Math.sqrt(Math.max(d * d - along * along, 0));
    // B = (a, 0) lies on the side (uy, -ux)·B > 0 or < 0 of AC; D goes to the other side.
    const side = a * uy > 0 ? -1 : 1;
    const dx = along * ux + side * across * uy;
    const dy = along * uy - side * across * ux;
    const expected = Math.hypot(dx - a, dy);
    if (room.diagonal2 && Math.abs(expected - room.diagonal2) > DIAGONAL_TOLERANCE) {
      warnings.push({
        code: "DIAGONAL_MISMATCH",
        expected: Math.round(expected),
        measured: room.diagonal2
      });
    }
    return {
      area: Math.round(first + second),
      perimeter: a + b + c + d,
      points: [
        [0, 0],
        [a, 0],
        [cx, cy],
        [dx, dy]
      ],
      expectedDiagonal2: Math.round(expected)
    };
  }

  const walls = room.walls.filter(wall => wall.length > 0);
  if (walls.length < 3) {
    warnings.push({ code: "MISSING_DIMENSIONS" });
    return null;
  }
  const points = polygonFromWalls(walls);
  const [x, y] = points[points.length - 1];
  const gap = Math.hypot(x, y);
  if (gap > CLOSURE_TOLERANCE) warnings.push({ code: "NOT_CLOSED", gap: Math.round(gap) });
  // The last point should be the first again; a small gap is closed by a straight line.
  const outline = gap < 1 ? points.slice(0, -1) : points;
  return {
    area: Math.round(shoelace(outline)),
    perimeter: walls.reduce((sum, wall) => sum + wall.length, 0),
    points: outline,
    closingGap: Math.round(gap)
  };
};

const toM = mm => Math.round(mm / 10) / 100;
const toM2 = mm2 => Math.round(mm2 / 10000) / 100;
const toM3 = mm3 => Math.round(mm3 / 10000000) / 100;

/**
 * Every figure of one room. `rules` decides which openings and cut-outs are deducted. Raw
 * values are mm/mm²/mm³; `figures` are m/m²/m³ rounded to two decimals.
 */
const summarizeRoom = (input, rulesInput) => {
  const room = normalizeRoom(input);
  const rules = normalizeRules(rulesInput);
  const warnings = [];
  const shape = measureShape(room, warnings);
  const wallLimit = rules.wallDeductOver * MM2_PER_M2;
  const floorLimit = rules.floorDeductOver * MM2_PER_M2;

  const floorGross = shape ? shape.area : 0;
  const perimeter = shape ? shape.perimeter : 0;
  const height = room.height;
  if (shape && !height) warnings.push({ code: "NO_HEIGHT" });
  const wallGross = perimeter * height;

  let openingsDeducted = 0;
  let openingsKept = 0;
  let reveals = 0;
  let doorWidths = 0;
  room.openings.forEach(opening => {
    const piece = opening.width * opening.height;
    if (!piece) return;
    if (opening.kind === "door") doorWidths += opening.width * opening.count;
    // VOB: an opening "over" the limit is deducted; its reveals are then added separately.
    if (piece > wallLimit) {
      openingsDeducted += piece * opening.count;
      reveals += (2 * opening.height + opening.width) * opening.depth * opening.count;
    } else {
      openingsKept += piece * opening.count;
    }
  });

  const adjust = { floor: 0, wall: 0, ceiling: 0 };
  room.adjustments.forEach(item => {
    const piece = item.length * item.width;
    if (!piece) return;
    const limit = item.target === "floor" ? floorLimit : wallLimit;
    if (item.sign > 0) adjust[item.target] += piece * item.count;
    else if (piece > limit) adjust[item.target] -= piece * item.count;
  });

  const clamp = (value, code) => {
    if (value >= 0) return value;
    warnings.push({ code });
    return 0;
  };
  const floorNet = clamp(floorGross + adjust.floor, "NEGATIVE_FLOOR");
  const ceilingNet = clamp(floorGross + adjust.ceiling, "NEGATIVE_CEILING");
  const wallNet = height
    ? clamp(wallGross - openingsDeducted + reveals + adjust.wall, "NEGATIVE_WALL")
    : 0;
  const skirting = Math.max(perimeter - doorWidths, 0);
  const volume = floorGross * height;

  return {
    valid: Boolean(shape),
    warnings,
    points: shape ? shape.points : [],
    closingGap: shape?.closingGap ?? null,
    expectedDiagonal2: shape?.expectedDiagonal2 ?? null,
    raw: {
      floorGross,
      floorNet,
      ceilingNet,
      perimeter,
      wallGross,
      openingsDeducted,
      openingsKept,
      reveals,
      wallNet,
      skirting,
      volume
    },
    figures: {
      floorGross: toM2(floorGross),
      floorNet: toM2(floorNet),
      ceilingNet: toM2(ceilingNet),
      perimeter: toM(perimeter),
      wallGross: toM2(wallGross),
      openingsDeducted: toM2(openingsDeducted),
      openingsKept: toM2(openingsKept),
      reveals: toM2(reveals),
      wallNet: toM2(wallNet),
      skirting: toM(skirting),
      volume: toM3(volume)
    }
  };
};

const FIGURE_KEYS = [
  "floorGross",
  "floorNet",
  "ceilingNet",
  "perimeter",
  "wallGross",
  "openingsDeducted",
  "openingsKept",
  "reveals",
  "wallNet",
  "skirting",
  "volume"
];

/**
 * All rooms plus totals. Totals add the rounded room figures (in whole hundredths), so they
 * match the sum of the lines on the printed Aufmaß.
 */
const summarizeMeasurement = (rooms, rulesInput) => {
  const summaries = (Array.isArray(rooms) ? rooms : []).map(room =>
    summarizeRoom(room, rulesInput)
  );
  const totals = {};
  FIGURE_KEYS.forEach(key => {
    const hundredths = summaries.reduce(
      (sum, summary) => sum + Math.round(summary.figures[key] * 100),
      0
    );
    totals[key] = hundredths / 100;
  });
  return {
    rooms: summaries,
    totals,
    roomCount: summaries.length,
    warningCount: summaries.reduce((sum, summary) => sum + summary.warnings.length, 0)
  };
};

/**
 * Reads a typed length into millimetres: "3,45" and "3.45" in `unit` (m by default), "345 cm",
 * "3455mm", "3 m 45" (= 3.45 m, and "3 m 5" = 3.05 m as spoken). Empty gives null, anything unreadable NaN.
 */
const parseLength = (text, unit = "m") => {
  if (typeof text === "number") return Number.isFinite(text) ? text : NaN;
  const value = String(text ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!value) return null;
  const factor = { m: 1000, cm: 10, mm: 1 };
  const mixed = value.match(/^(\d+) ?m ?(\d{1,2}) ?(cm)?$/);
  if (mixed) return Number(mixed[1]) * 1000 + Number(mixed[2]) * 10;
  const plain = value.replace(",", ".").match(/^(\d+(?:\.\d*)?|\.\d+) ?(mm|cm|m)?$/);
  if (!plain) return NaN;
  const mm = Number(plain[1]) * factor[plain[2] || unit];
  return Number.isFinite(mm) ? Math.round(mm) : NaN;
};

export {
  SHAPES,
  OPENING_KINDS,
  ADJUSTMENT_TARGETS,
  MAX_LENGTH,
  CLOSURE_TOLERANCE,
  DIAGONAL_TOLERANCE,
  DEDUCTION_PRESETS,
  DEFAULT_RULES,
  FIGURE_KEYS,
  normalizeRules,
  normalizeRoom,
  polygonFromWalls,
  triangleArea,
  closingWall,
  summarizeRoom,
  summarizeMeasurement,
  parseLength
};
